import { TFunction } from 'i18next';

export interface PasswordRequirement {
  met: (password: string) => boolean;
  message: string;
  mandatory?: boolean;
}

const minPasswordReqs = 4;

export const passwordRequirements = (
  t?: TFunction<'common', 'form_field.password.requirements'>,
): PasswordRequirement[] => [
  {
    met: (password) => password.length >= 8,
    message: t?.('length') ?? '',
    mandatory: true,
  },
  {
    met: (password) => /[a-z]/.test(password),
    message: t?.('lowercase') ?? '',
    mandatory: true,
  },
  {
    met: (password) => /[A-Z]/.test(password),
    message: t?.('uppercase') ?? '',
    mandatory: true,
  },
  {
    met: (password) => /\d/.test(password),
    message: t?.('digit') ?? '',
    mandatory: true,
  },
  {
    met: (password) => /[@$!%*?&#]/.test(password),
    message: t?.('special_char') ?? '',
    mandatory: true,
  },
];

export function checkPasswordRequirements(
  password: string,
  t?: TFunction<'common', 'form_field.password.requirements'>,
) {
  return passwordRequirements(t).map((req) => ({
    met: req.met(password),
    message: req.message,
    mandatory: req.mandatory,
  }));
}

export function isPasswordValid(password: string) {
  const requirements = checkPasswordRequirements(password);
  const mandatoryMet = requirements.every((req) => !req.mandatory || req.met);
  const totalMet = requirements.filter((req) => req.met).length;

  return mandatoryMet && totalMet >= minPasswordReqs;
}

export function getPasswordStrength(password: string) {
  if (!password) return 0;
  return passwordRequirements().filter((req) => req.met(password)).length;
}

export function getStrengthColor(strength: number) {
  return strength <= 3 ? 'error-red' : 'success-green';
}

export function getPasswordStatusText(
  strength: number,
  t?: TFunction<'common', 'form_field.password.requirements'>,
) {
  return strength <= 3 ? (t?.('weak') ?? '') : (t?.('strong') ?? '');
}
